import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import SearchableDropdown from "src/common/components/SearchableDropdown/SearchableDropdown";
import type { SearchableDropdownOptionGroup } from "src/common/components/SearchableDropdown/SearchableDropdown";
import SegmentedControl from "src/common/components/SegmentedControl/SegmentedControl";
import { AudioPreviewButton } from "src/common/components/Input/AudioPreviewButton";
import type { IElevenlabsVoice } from "../state/gallerySlice";
import { TTS_PROVIDER_LABELS } from "./GenerateTtsPanel";
import type { TtsProviderFilter } from "./GenerateTtsPanel";

type GenderFilter = "all" | "male" | "female";

interface VoicePickerProps {
  label: string;
  voices: IElevenlabsVoice[];
  selectedVoiceId: string;
  onSelect: (voice: IElevenlabsVoice) => void;
  placement?: "bottom" | "top";
}

export const VoicePicker = ({
  label,
  voices,
  selectedVoiceId,
  onSelect,
  placement = "bottom",
}: VoicePickerProps) => {
  const { t } = useTranslation();
  const [genderFilter, setGenderFilter] = useState<GenderFilter>("all");
  const [providerFilter, setProviderFilter] = useState<TtsProviderFilter>("all");

  const selectedVoice = voices.find((voice) => voice.id === selectedVoiceId);
  const hasGradiumVoices = voices.some((voice) => voice.tts_provider === "gradium");

  const optionGroups = useMemo(() => {
    const toOption = (voice: IElevenlabsVoice) => ({
      id: voice.id,
      name: `${voice.name} (${t(`general.${voice.gender}`, {
        defaultValue: t("general.unknown"),
      })})`,
    });

    const groups: Record<string, SearchableDropdownOptionGroup> = {};
    voices.forEach((voice) => {
      if (voice.id === selectedVoiceId) return;
      if (genderFilter !== "all" && voice.gender !== genderFilter) return;
      if (providerFilter !== "all" && voice.tts_provider !== providerFilter) return;
      groups[voice.tts_provider] ??= {
        label: TTS_PROVIDER_LABELS[voice.tts_provider] || voice.tts_provider,
        options: [],
      };
      groups[voice.tts_provider].options.push(toOption(voice));
    });

    const current = voices.find((voice) => voice.id === selectedVoiceId);
    return [
      ...(current
        ? [
            {
              label: t("pages.gallery.generateVoicePanel.currentSelection"),
              options: [toOption(current)],
            },
          ]
        : []),
      ...Object.values(groups),
    ];
  }, [voices, selectedVoiceId, genderFilter, providerFilter, t]);

  const options = useMemo(() => optionGroups.flatMap((group) => group.options), [optionGroups]);

  return (
    <div>
      <label className="block font-medium text-gray-700 text-md mt-2">
        {`${label} (${voices.length})`}
      </label>
      <div className="flex items-center">
        <SearchableDropdown
          options={options}
          optionGroups={optionGroups}
          selectedId={selectedVoiceId}
          onSelect={(selectedId) => {
            const picked = voices.find((voice) => voice.id === selectedId);
            if (picked) onSelect(picked);
          }}
          placeholder={t("general.pleaseSelect")}
          searchPlaceholder={t("pages.gallery.generateVoicePanel.searchVoice")}
          noItemFound={
            <span className="block py-10 text-center">
              {t("pages.gallery.generateVoicePanel.noVoiceFound")}
            </span>
          }
          listMaxHeightClassName="h-64"
          placement={placement}
          onOpenChange={(isOpen) => {
            if (!isOpen) {
              setGenderFilter("all");
              setProviderFilter("all");
            }
          }}
          filterSlot={
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 w-14 shrink-0">
                  {t("pages.gallery.generateVoicePanel.filterGender")}
                </span>
                <SegmentedControl<GenderFilter>
                  options={[
                    { value: "all", label: t("general.all") },
                    { value: "male", label: t("general.male") },
                    { value: "female", label: t("general.female") },
                  ]}
                  value={genderFilter}
                  onChange={setGenderFilter}
                  size="sm"
                  variant="outlined"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 w-14 shrink-0">
                  {t("pages.gallery.generateVoicePanel.filterSource")}
                </span>
                <SegmentedControl<TtsProviderFilter>
                  options={[
                    { value: "all", label: t("general.all") },
                    { value: "elevenLabs", label: TTS_PROVIDER_LABELS.elevenLabs },
                    { value: "google", label: TTS_PROVIDER_LABELS.google },
                    ...(hasGradiumVoices
                      ? [{ value: "gradium" as const, label: TTS_PROVIDER_LABELS.gradium }]
                      : []),
                  ]}
                  value={providerFilter}
                  onChange={setProviderFilter}
                  size="sm"
                  variant="outlined"
                />
              </div>
            </div>
          }
        />
        {selectedVoice?.audio_preview_url && (
          <AudioPreviewButton url={selectedVoice.audio_preview_url} className="ml-[6px]" />
        )}
      </div>
    </div>
  );
};
